import React,{useState,useEffect}from'react';
import{useNavigate,useParams}from'react-router-dom';
import Layout from'../components/layout/Layout';
import api from'../services/api';
import {useTranslation} from 'react-i18next';
import FileShaClaimButton from '../components/common/FileShaClaimButton';
import PatientSearchInput from '../components/patient/PatientSearchInput';

const INP=({value,onChange,type='text',placeholder='',disabled=false,required=false}:any)=>(
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} required={required}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#015CA6] focus:ring-2 focus:ring-[#015CA6]/10 disabled:bg-gray-50"/>
);
const SEL=({value,onChange,children,disabled=false}:any)=>(
  <select value={value} onChange={onChange} disabled={disabled}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none disabled:bg-gray-50">
    {children}
  </select>
);
const FLD=({label,children,required=false,span=1}:any)=>(
  <div className={span===2?'col-span-2':span===3?'col-span-3':''}>
    <label className="block text-xs font-semibold text-gray-600 mb-1">
      {label}{required&&<span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);
const SEC=({title,color='#02357A',children}:any)=>(
  <div className="bg-white rounded-xl border shadow-sm overflow-hidden mb-4">
    <div className="px-5 py-3 border-b" style={{background:color}}>
      <h3 className="font-bold text-white text-sm">{title}</h3>
    </div>
    <div className="p-5 grid grid-cols-3 gap-4">{children}</div>
  </div>
);

const TRIAGE_COLORS=['P1 — RED (Immediate)','P2 — YELLOW (Urgent)','P3 — GREEN (Minor)','P4 — BLACK (Expectant)','P5 — WHITE (Dead on Arrival)'];
const MODES=['WALK_IN','AMBULANCE','POLICE','REFERRED','TRANSFER','BROUGHT_DEAD'];

const calcAgeYears=(dob?:string)=>{
  if(!dob)return'';
  const y=Math.floor((Date.now()-new Date(dob).getTime())/31536000000);
  return y>0?String(y):'';
};

export default function EmergencyRegistrationPage(){
  const {t}=useTranslation();
  const nav=useNavigate();
  const {id}=useParams();
  const isEdit=!!id;
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [error,setError]=useState('');
  const [doctors,setDoctors]=useState<any[]>([]);
  const [lastCase,setLastCase]=useState<any>(null);

  const [form,setForm]=useState({
    // Patient identity
    caseNumber:'',patientId:'',patientName:'',patientAge:'',patientAgeUnit:'YEARS',
    patientGender:'MALE',patientPhone:'',patientAddress:'',
    relativePhone:'',relativeName:'',relativeRelation:'',
    // Triage
    triageLevel:'P2',triageColor:'YELLOW',esiLevel:'2',
    assessmentTime:new Date().toISOString().slice(0,16),
    chiefComplaint:'',presentingComplaints:'',historyOfPresentIllness:'',
    // Mode of arrival
    modeOfArrival:'WALK_IN',referralSource:'',referralHospital:'',
    ambulanceNumber:'',driverName:'',driverMobile:'',
    // Triage vitals
    bp:'',heartRate:'',respiratoryRate:'',spo2:'',temperature:'',
    gcsEyes:'4',gcsVerbal:'5',gcsMotor:'6',totalGcs:'15',
    painScore:'0',bloodSugar:'',weight:'',
    // Attending doctor
    attendingDoctorId:'',attendingNurseId:'',
    // Clinical
    examinationFindings:'',workingDiagnosis:'',treatmentGiven:'',
    ivAccess:'',ivFluidGiven:'',o2Given:false,o2Flow:'',
    ecgDone:false,ecgFindings:'',xrayOrdered:false,
    // MLC
    mlcCase:false,mlcNumber:'',mlcType:'',incidentDateTime:'',
    incidentLocation:'',policeOfficerName:'',policeStation:'',
    policeInformed:false,policeInformedAt:'',fir:'',
    // Code blue
    codeBlueCalled:false,codeBlueTime:'',
    // Trauma
    isTrauma:false,traumaType:'',mechanism:'',
    accidentLocation:'',injuryDescription:'',
    // Disposition
    disposition:'UNDER_OBSERVATION',dispositionTime:'',
    dispositionDoctor:'',dispositionWard:'',dispositionBed:'',
    referredTo:'',transferReason:'',
    // Status
    status:'REGISTERED',priority:'NORMAL',
  });
  const f=(k:string,v:any)=>setForm(x=>({...x,[k]:v}));

  useEffect(()=>{
    api.getDoctors?.().then((r:any)=>setDoctors(Array.isArray(r?.data)?r.data:r?.data?.data||[])).catch(()=>{});
    if(isEdit){
      api.getEmergencyCase?.(id!).then((r:any)=>{
        const d=r?.data||{};
        setForm(prev=>({...prev,...Object.fromEntries(Object.entries(d).map(([k,v])=>[k,v??'']))}));
      }).catch(()=>{});
    }
  },[id,isEdit]);

  // Auto-calculate GCS
  useEffect(()=>{
    const total=+form.gcsEyes+(+form.gcsVerbal)+(+form.gcsMotor);
    f('totalGcs',String(total||15));
  },[form.gcsEyes,form.gcsVerbal,form.gcsMotor]);

  const submit=async()=>{
    if(!form.patientName||!form.triageLevel){
      setError('Patient name and triage level are required');return;
    }
    setSaving(true);setError('');
    try{
      const payload={
        // Patient
        caseNumber:form.caseNumber||null,patientId:form.patientId||null,
        patientName:form.patientName,patientAge:form.patientAge?+form.patientAge:null,
        patientAgeUnit:form.patientAgeUnit,patientGender:form.patientGender,
        patientPhone:form.patientPhone||null,patientAddress:form.patientAddress||null,
        relativePhone:form.relativePhone||null,relativeName:form.relativeName||null,
        relativeRelation:form.relativeRelation||null,
        // Triage
        triageLevel:form.triageLevel,triageColor:form.triageColor,esiLevel:form.esiLevel?+form.esiLevel:null,
        assessmentTime:form.assessmentTime||new Date().toISOString(),
        chiefComplaint:form.chiefComplaint||null,presentingComplaints:form.presentingComplaints||null,
        historyOfPresentIllness:form.historyOfPresentIllness||null,
        // Mode
        modeOfArrival:form.modeOfArrival,referralSource:form.referralSource||null,
        referralHospital:form.referralHospital||null,ambulanceNumber:form.ambulanceNumber||null,
        // Vitals
        vitalSigns:{bp:form.bp,heartRate:form.heartRate,respiratoryRate:form.respiratoryRate,
          spo2:form.spo2,temperature:form.temperature,gcs:form.totalGcs,
          painScore:form.painScore,bloodSugar:form.bloodSugar,weight:form.weight},
        gcsEyes:form.gcsEyes?+form.gcsEyes:null,gcsVerbal:form.gcsVerbal?+form.gcsVerbal:null,
        gcsMotor:form.gcsMotor?+form.gcsMotor:null,totalGcs:form.totalGcs?+form.totalGcs:null,
        // Doctor
        attendingDoctorId:form.attendingDoctorId||null,
        // Clinical
        examinationFindings:form.examinationFindings||null,workingDiagnosis:form.workingDiagnosis||null,
        treatmentGiven:form.treatmentGiven||null,ivAccess:form.ivAccess||null,
        ivFluidGiven:form.ivFluidGiven||null,o2Given:form.o2Given,o2Flow:form.o2Flow||null,
        ecgDone:form.ecgDone,ecgFindings:form.ecgFindings||null,
        // MLC
        mlcCase:form.mlcCase,mlcNumber:form.mlcCase?form.mlcNumber||null:null,
        mlcType:form.mlcCase?form.mlcType||null:null,
        incidentDateTime:form.mlcCase?form.incidentDateTime||null:null,
        incidentLocation:form.mlcCase?form.incidentLocation||null:null,
        policeOfficerName:form.mlcCase?form.policeOfficerName||null:null,
        policeStation:form.mlcCase?form.policeStation||null:null,
        policeInformed:form.policeInformed,policeInformedAt:form.policeInformedAt||null,
        fir:form.fir||null,
        // Trauma
        isTrauma:form.isTrauma,traumaType:form.isTrauma?form.traumaType||null:null,
        mechanism:form.isTrauma?form.mechanism||null:null,
        accidentLocation:form.isTrauma?form.accidentLocation||null:null,
        injuryDescription:form.isTrauma?form.injuryDescription||null:null,
        // Code blue
        codeBlueCalled:form.codeBlueCalled,codeBlueTime:form.codeBlueTime||null,
        // Disposition
        disposition:form.disposition,dispositionTime:form.dispositionTime||null,
        dispositionDoctor:form.dispositionDoctor||null,dispositionWard:form.dispositionWard||null,
        dispositionBed:form.dispositionBed||null,referredTo:form.referredTo||null,
        transferReason:form.transferReason||null,
        status:form.status,priority:form.priority,
      };
      if(isEdit){
        await api.updateEmergencyCase?.(id!,payload);
        setMsg('✅ Case updated');
      } else {
        const r=await api.registerEmergencyCase?.(payload);
        const created=r?.data||{};
        setLastCase(created);
        setMsg(`✅ Emergency case registered! Case no: ${created.case_number||created.caseNumber||'Assigned'}`);
      }
    }catch(e:any){setError(e.response?.data?.message||'Registration failed');}
    setSaving(false);
  };

  const triageStyles:{[k:string]:{bg:string;border:string;text:string}}={
    'P1 — RED (Immediate)':{bg:'#fef2f2',border:'#dc2626',text:'#b91c1c'},
    'P2 — YELLOW (Urgent)':{bg:'#fefce8',border:'#ca8a04',text:'#92400e'},
    'P3 — GREEN (Minor)':{bg:'#f0fdf4',border:'#16a34a',text:'#15803d'},
    'P4 — BLACK (Expectant)':{bg:'#f9fafb',border:'#111827',text:'#111827'},
    'P5 — WHITE (Dead on Arrival)':{bg:'#f8fafc',border:'#94a3b8',text:'#64748b'},
  };
  const selectedTriage=triageStyles[form.triageLevel]||triageStyles['P2 — YELLOW (Urgent)'];

  return(
    <Layout title={isEdit?'Edit Emergency Case':'Emergency Registration'} actions={
      <div className="flex gap-2">
        <button onClick={()=>nav('/emergency')} className="px-4 py-1.5 border rounded-lg text-sm hover:bg-gray-50">← Emergency</button>
        <button onClick={submit} disabled={saving}
          className="px-5 py-1.5 rounded-lg text-sm font-bold text-white disabled:opacity-50"
          style={{background:'#dc2626'}}>
          {saving?'Saving…':'🚨 Register Case'}
        </button>
      </div>
    }>
      <div className="p-4 max-w-6xl mx-auto">
        {error&&<div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>}
        {msg&&<div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm font-bold flex flex-wrap items-center justify-between gap-3">
          <span>{msg}</span>
          {lastCase?.id&&(
            <FileShaClaimButton
              patientId={lastCase.patientId||lastCase.patient_id||form.patientId||''}
              patientName={form.patientName}
              encounterId={lastCase.id}
              serviceName={form.chiefComplaint||'Emergency'}
              serviceType="EMERGENCY"
              from="emergency"
            />
          )}
        </div>}

        {/* Triage Banner */}
        <div className="p-4 rounded-xl mb-4 border-2 flex items-center gap-4 flex-wrap"
          style={{background:selectedTriage.bg,borderColor:selectedTriage.border}}>
          <div>
            <p className="text-xs font-bold uppercase" style={{color:selectedTriage.text}}>Triage Level</p>
            <p className="text-2xl font-black" style={{color:selectedTriage.text}}>{form.triageLevel}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {TRIAGE_COLORS.map(t=>(
              <button key={t} onClick={()=>f('triageLevel',t)}
                className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-all ${form.triageLevel===t?'shadow-lg scale-105':''}`}
                style={form.triageLevel===t?{background:selectedTriage.border,color:'white',borderColor:selectedTriage.border}:{borderColor:'#e5e7eb',color:'#374151'}}>
                {t.split(' — ')[0]}
              </button>
            ))}
          </div>
        </div>

        {/* Patient Identity */}
        <SEC title="👤 Patient Identity">
          <FLD label="Link HMIS patient (SHA S7)" span={3}>
            <PatientSearchInput
              label=""
              selected={form.patientId ? { id: form.patientId, firstName: form.patientName } : null}
              onSelect={(p:any)=>{
                const nm=`${p.firstName||p.first_name||''} ${p.lastName||p.last_name||''}`.trim();
                setForm(x=>({
                  ...x,
                  patientId:p.id,
                  patientName:nm||x.patientName,
                  patientPhone:p.phone||x.patientPhone,
                  patientGender:(p.gender||x.patientGender||'MALE').toUpperCase(),
                  patientAge:p.age||calcAgeYears(p.dateOfBirth||p.date_of_birth)||x.patientAge,
                }));
              }}
              onClear={()=>setForm(x=>({...x,patientId:''}))}
              placeholder="Search registered patient — leave empty if unidentified (S8)"
            />
            <p className="text-[11px] text-slate-500 mt-1">{form.patientId ? 'Linked — SHA will open as S7 ECCIF (identified).' : 'No HMIS link — SHA auto-claim is S8 unidentified ECCIF until you identify them on the claim.'}</p>
          </FLD>
          <FLD label="Case Number (Auto)"><INP value={form.caseNumber} onChange={(e:any)=>f('caseNumber',e.target.value)} placeholder="Auto-generated"/></FLD>
          <FLD label="Patient Name" required><INP value={form.patientName} onChange={(e:any)=>f('patientName',e.target.value)} placeholder="Full name or Unknown"/></FLD>
          <FLD label="Age"><div className="flex gap-2"><INP type="number" value={form.patientAge} onChange={(e:any)=>f('patientAge',e.target.value)} placeholder="Age"/><SEL value={form.patientAgeUnit} onChange={(e:any)=>f('patientAgeUnit',e.target.value)}><option>YEARS</option><option>MONTHS</option><option>DAYS</option></SEL></div></FLD>
          <FLD label="Gender"><SEL value={form.patientGender} onChange={(e:any)=>f('patientGender',e.target.value)}><option>MALE</option><option>FEMALE</option><option>TRANSGENDER</option><option>UNKNOWN</option></SEL></FLD>
          <FLD label="Phone"><INP value={form.patientPhone} onChange={(e:any)=>f('patientPhone',e.target.value)} placeholder="Mobile number"/></FLD>
          <FLD label="Address"><INP value={form.patientAddress} onChange={(e:any)=>f('patientAddress',e.target.value)} placeholder="Address"/></FLD>
          <FLD label="Relative/Attendant Name"><INP value={form.relativeName} onChange={(e:any)=>f('relativeName',e.target.value)} placeholder="Name"/></FLD>
          <FLD label="Relative Phone"><INP value={form.relativePhone} onChange={(e:any)=>f('relativePhone',e.target.value)} placeholder="Mobile"/></FLD>
          <FLD label="Relation"><SEL value={form.relativeRelation} onChange={(e:any)=>f('relativeRelation',e.target.value)}><option value="">{t('common.select','Select')}</option>{['SPOUSE','FATHER','MOTHER','SON','DAUGHTER','SIBLING','FRIEND','UNKNOWN'].map(r=><option key={r}>{r}</option>)}</SEL></FLD>
        </SEC>

        {/* Arrival */}
        <SEC title="🚑 Mode of Arrival">
          <FLD label="Mode" required><SEL value={form.modeOfArrival} onChange={(e:any)=>f('modeOfArrival',e.target.value)}>{MODES.map(m=><option key={m}>{m}</option>)}</SEL></FLD>
          <FLD label="Assessment Time"><INP type="datetime-local" value={form.assessmentTime} onChange={(e:any)=>f('assessmentTime',e.target.value)}/></FLD>
          {form.modeOfArrival==='AMBULANCE'&&<><FLD label="Ambulance No."><INP value={form.ambulanceNumber} onChange={(e:any)=>f('ambulanceNumber',e.target.value)} placeholder="AMB-01"/></FLD><FLD label="Driver Name"><INP value={form.driverName} onChange={(e:any)=>f('driverName',e.target.value)}/></FLD><FLD label="Driver Mobile"><INP value={form.driverMobile} onChange={(e:any)=>f('driverMobile',e.target.value)}/></FLD></>}
          {['REFERRED','TRANSFER'].includes(form.modeOfArrival)&&<><FLD label="Referred From Hospital"><INP value={form.referralHospital} onChange={(e:any)=>f('referralHospital',e.target.value)} placeholder="Hospital name"/></FLD><FLD label="Referral Source"><INP value={form.referralSource} onChange={(e:any)=>f('referralSource',e.target.value)} placeholder="Doctor/Clinic name"/></FLD></>}
          <FLD label="Attending Doctor"><SEL value={form.attendingDoctorId} onChange={(e:any)=>f('attendingDoctorId',e.target.value)}><option value="">Assign Doctor</option>{doctors.map((d:any)=><option key={d.id} value={d.id}>Dr. {d.first_name} {d.last_name} ({d.specialization||'—'})</option>)}</SEL></FLD>
          <FLD label="Priority"><SEL value={form.priority} onChange={(e:any)=>f('priority',e.target.value)}><option>NORMAL</option><option>URGENT</option><option>CRITICAL</option></SEL></FLD>
          <FLD label="ESI Level (1-5)"><SEL value={form.esiLevel} onChange={(e:any)=>f('esiLevel',e.target.value)}><option value="1">1 — Resuscitation</option><option value="2">2 — Emergent</option><option value="3">3 — Urgent</option><option value="4">4 — Less Urgent</option><option value="5">5 — Non-urgent</option></SEL></FLD>
        </SEC>

        {/* Chief Complaint */}
        <SEC title="📋 Chief Complaint & History">
          <FLD label="Chief Complaint" required span={3}><textarea value={form.chiefComplaint} onChange={e=>f('chiefComplaint',e.target.value)} rows={2} placeholder="Main presenting complaint..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none"/></FLD>
          <FLD label="Presenting Complaints (detailed)" span={3}><textarea value={form.presentingComplaints} onChange={e=>f('presentingComplaints',e.target.value)} rows={2} placeholder="Detailed symptoms, onset, duration, severity..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none"/></FLD>
          <FLD label="History of Present Illness" span={3}><textarea value={form.historyOfPresentIllness} onChange={e=>f('historyOfPresentIllness',e.target.value)} rows={2} placeholder="H/O present illness..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none"/></FLD>
        </SEC>

        {/* Triage Vitals */}
        <SEC title="💊 Triage Vitals" color="#039089">
          <FLD label="BP (mmHg)"><INP value={form.bp} onChange={(e:any)=>f('bp',e.target.value)} placeholder="120/80"/></FLD>
          <FLD label="Heart Rate (bpm)"><INP type="number" value={form.heartRate} onChange={(e:any)=>f('heartRate',e.target.value)} placeholder="80"/></FLD>
          <FLD label="RR (breaths/min)"><INP type="number" value={form.respiratoryRate} onChange={(e:any)=>f('respiratoryRate',e.target.value)} placeholder="16"/></FLD>
          <FLD label="SpO2 (%)"><INP type="number" value={form.spo2} onChange={(e:any)=>f('spo2',e.target.value)} placeholder="98"/></FLD>
          <FLD label="Temperature (°F)"><INP type="number" value={form.temperature} onChange={(e:any)=>f('temperature',e.target.value)} placeholder="98.6"/></FLD>
          <FLD label="Blood Sugar (mg/dL)"><INP type="number" value={form.bloodSugar} onChange={(e:any)=>f('bloodSugar',e.target.value)} placeholder=""/></FLD>
          <FLD label="Pain Score (0-10)"><SEL value={form.painScore} onChange={(e:any)=>f('painScore',e.target.value)}>{[0,1,2,3,4,5,6,7,8,9,10].map(n=><option key={n}>{n}</option>)}</SEL></FLD>
          <FLD label="Weight (kg)"><INP type="number" value={form.weight} onChange={(e:any)=>f('weight',e.target.value)} placeholder=""/></FLD>
          {/* GCS */}
          <div className="col-span-3 border rounded-xl p-3 bg-gray-50">
            <p className="text-xs font-bold text-gray-600 mb-2">Glasgow Coma Scale (GCS) — Total: <span className="text-[#02357A] text-base">{form.totalGcs}</span></p>
            <div className="grid grid-cols-3 gap-3">
              <FLD label="Eyes (E) 1-4"><SEL value={form.gcsEyes} onChange={(e:any)=>f('gcsEyes',e.target.value)}><option value="4">4 — Spontaneous</option><option value="3">3 — To Voice</option><option value="2">2 — To Pain</option><option value="1">1 — None</option></SEL></FLD>
              <FLD label="Verbal (V) 1-5"><SEL value={form.gcsVerbal} onChange={(e:any)=>f('gcsVerbal',e.target.value)}><option value="5">5 — Oriented</option><option value="4">4 — Confused</option><option value="3">3 — Words</option><option value="2">2 — Sounds</option><option value="1">1 — None</option></SEL></FLD>
              <FLD label="Motor (M) 1-6"><SEL value={form.gcsMotor} onChange={(e:any)=>f('gcsMotor',e.target.value)}><option value="6">6 — Obeys Commands</option><option value="5">5 — Localizes</option><option value="4">4 — Withdraws</option><option value="3">3 — Flexion</option><option value="2">2 — Extension</option><option value="1">1 — None</option></SEL></FLD>
            </div>
          </div>
        </SEC>

        {/* Clinical Management */}
        <SEC title="🩺 Initial Assessment & Management">
          <FLD label="Examination Findings" span={3}><textarea value={form.examinationFindings} onChange={e=>f('examinationFindings',e.target.value)} rows={2} placeholder="Physical examination findings..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none"/></FLD>
          <FLD label="Working Diagnosis" span={2}><INP value={form.workingDiagnosis} onChange={(e:any)=>f('workingDiagnosis',e.target.value)} placeholder="Working diagnosis on arrival"/></FLD>
          <FLD label="Treatment Given"><INP value={form.treatmentGiven} onChange={(e:any)=>f('treatmentGiven',e.target.value)} placeholder="Immediate treatment given"/></FLD>
          <FLD label="IV Access"><SEL value={form.ivAccess} onChange={(e:any)=>f('ivAccess',e.target.value)}><option value="">Not Done</option><option>PERIPHERAL_18G</option><option>PERIPHERAL_20G</option><option>CENTRAL_LINE</option><option>IO_ACCESS</option></SEL></FLD>
          <FLD label="IV Fluid Given"><INP value={form.ivFluidGiven} onChange={(e:any)=>f('ivFluidGiven',e.target.value)} placeholder="NS 500ml, DNS 500ml..."/></FLD>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.o2Given} onChange={e=>f('o2Given',e.target.checked)} className="w-4 h-4 rounded"/><span className="text-sm font-medium">O2 Given</span></label>
            {form.o2Given&&<div className="flex-1"><INP value={form.o2Flow} onChange={(e:any)=>f('o2Flow',e.target.value)} placeholder="Flow rate (L/min)"/></div>}
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.ecgDone} onChange={e=>f('ecgDone',e.target.checked)} className="w-4 h-4 rounded"/><span className="text-sm font-medium">ECG Done</span></label>
            {form.ecgDone&&<div className="flex-1"><INP value={form.ecgFindings} onChange={(e:any)=>f('ecgFindings',e.target.value)} placeholder="ECG findings"/></div>}
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.codeBlueCalled} onChange={e=>f('codeBlueCalled',e.target.checked)} className="w-4 h-4 rounded"/><span className="text-sm font-bold text-red-700">Code Blue Called</span></label>
            {form.codeBlueCalled&&<INP type="datetime-local" value={form.codeBlueTime} onChange={(e:any)=>f('codeBlueTime',e.target.value)}/>}
          </div>
        </SEC>

        {/* Trauma */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden mb-4">
          <div className="px-5 py-3 border-b flex items-center gap-3" style={{background:'#7c3aed'}}>
            <input type="checkbox" checked={form.isTrauma} onChange={e=>f('isTrauma',e.target.checked)} className="w-4 h-4 rounded"/>
            <h3 className="font-bold text-white text-sm">🏥 Trauma Case</h3>
          </div>
          {form.isTrauma&&(
            <div className="p-5 grid grid-cols-3 gap-4">
              <FLD label="Trauma Type"><SEL value={form.traumaType} onChange={(e:any)=>f('traumaType',e.target.value)}><option value="">{t('common.select','Select')}</option>{['RTA','FALL','ASSAULT','BURN','DROWNING','POISONING','ELECTROCUTION','ANIMAL_BITE','OTHER'].map(t=><option key={t}>{t}</option>)}</SEL></FLD>
              <FLD label="Mechanism"><INP value={form.mechanism} onChange={(e:any)=>f('mechanism',e.target.value)} placeholder="How injury occurred"/></FLD>
              <FLD label="Accident Location"><INP value={form.accidentLocation} onChange={(e:any)=>f('accidentLocation',e.target.value)} placeholder="Where accident happened"/></FLD>
              <FLD label="Injury Description" span={3}><textarea value={form.injuryDescription} onChange={e=>f('injuryDescription',e.target.value)} rows={2} placeholder="Description of injuries..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none"/></FLD>
            </div>
          )}
        </div>

        {/* MLC */}
        <div className="bg-white rounded-xl border-2 border-red-200 shadow-sm overflow-hidden mb-4">
          <div className="px-5 py-3 border-b flex items-center gap-3 bg-red-600">
            <input type="checkbox" checked={form.mlcCase} onChange={e=>f('mlcCase',e.target.checked)} className="w-4 h-4 rounded"/>
            <h3 className="font-bold text-white text-sm">⚖️ Medico-Legal Case (MLC)</h3>
          </div>
          {form.mlcCase&&(
            <div className="p-5 grid grid-cols-3 gap-4">
              <FLD label="MLC Number"><INP value={form.mlcNumber} onChange={(e:any)=>f('mlcNumber',e.target.value)} placeholder="MLC-2025-001"/></FLD>
              <FLD label="MLC Type"><SEL value={form.mlcType} onChange={(e:any)=>f('mlcType',e.target.value)}><option value="">Select Type</option>{['RTA','ASSAULT','RAPE','POISONING','BURNS','FALL','INDUSTRIAL_ACCIDENT','DOMESTIC_VIOLENCE','UNKNOWN_CAUSE','OTHER'].map(t=><option key={t}>{t}</option>)}</SEL></FLD>
              <FLD label="Incident Date & Time"><INP type="datetime-local" value={form.incidentDateTime} onChange={(e:any)=>f('incidentDateTime',e.target.value)}/></FLD>
              <FLD label="Incident Location" span={2}><INP value={form.incidentLocation} onChange={(e:any)=>f('incidentLocation',e.target.value)} placeholder="Where the incident occurred"/></FLD>
              <div className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.policeInformed} onChange={e=>f('policeInformed',e.target.checked)} className="w-4 h-4"/><label className="text-sm font-medium">Police Informed</label></div>
              {form.policeInformed&&<>
                <FLD label="Police Station"><INP value={form.policeStation} onChange={(e:any)=>f('policeStation',e.target.value)} placeholder="Police station name"/></FLD>
                <FLD label="Officer Name"><INP value={form.policeOfficerName} onChange={(e:any)=>f('policeOfficerName',e.target.value)} placeholder="Officer name & rank"/></FLD>
                <FLD label="Police Informed At"><INP type="datetime-local" value={form.policeInformedAt} onChange={(e:any)=>f('policeInformedAt',e.target.value)}/></FLD>
                <FLD label="FIR Number"><INP value={form.fir} onChange={(e:any)=>f('fir',e.target.value)} placeholder="FIR number if registered"/></FLD>
              </>}
            </div>
          )}
        </div>

        {/* Disposition */}
        <SEC title="📤 Disposition">
          <FLD label="Disposition" required><SEL value={form.disposition} onChange={(e:any)=>f('disposition',e.target.value)}><option value="UNDER_OBSERVATION">Under Observation</option><option value="ADMIT_IPD">Admit to IPD</option><option value="ADMIT_ICU">Admit to ICU</option><option value="DISCHARGE">Discharge</option><option value="TRANSFER">Transfer to Another Hospital</option><option value="ABSCONDED">Absconded / LAMA</option><option value="EXPIRED">Expired</option><option value="BROUGHT_DEAD">Brought Dead</option></SEL></FLD>
          <FLD label="Disposition Time"><INP type="datetime-local" value={form.dispositionTime} onChange={(e:any)=>f('dispositionTime',e.target.value)}/></FLD>
          <FLD label="Status"><SEL value={form.status} onChange={(e:any)=>f('status',e.target.value)}><option>REGISTERED</option><option>UNDER_TREATMENT</option><option>UNDER_OBSERVATION</option><option>ADMITTED</option><option>DISCHARGED</option><option>TRANSFERRED</option><option>EXPIRED</option></SEL></FLD>
          {['ADMIT_IPD','ADMIT_ICU'].includes(form.disposition)&&<>
            <FLD label="Ward"><INP value={form.dispositionWard} onChange={(e:any)=>f('dispositionWard',e.target.value)} placeholder="Target ward"/></FLD>
            <FLD label="Bed"><INP value={form.dispositionBed} onChange={(e:any)=>f('dispositionBed',e.target.value)} placeholder="Bed number"/></FLD>
            <FLD label="Admitting Doctor"><INP value={form.dispositionDoctor} onChange={(e:any)=>f('dispositionDoctor',e.target.value)} placeholder="Doctor name"/></FLD>
          </>}
          {form.disposition==='TRANSFER'&&<>
            <FLD label="Referred To Hospital" span={2}><INP value={form.referredTo} onChange={(e:any)=>f('referredTo',e.target.value)} placeholder="Hospital name"/></FLD>
            <FLD label="Transfer Reason"><INP value={form.transferReason} onChange={(e:any)=>f('transferReason',e.target.value)} placeholder="Reason for transfer"/></FLD>
          </>}
        </SEC>

        <div className="flex gap-3 justify-end pb-8">
          <button onClick={()=>nav('/emergency')} className="px-6 py-2.5 border rounded-xl text-gray-600 hover:bg-gray-50">{t('common.cancel','Cancel')}</button>
          <button onClick={submit} disabled={saving}
            className="px-8 py-2.5 rounded-xl text-white font-bold disabled:opacity-50"
            style={{background:'#dc2626'}}>
            {saving?'Saving…':'🚨 Register Emergency Case'}
          </button>
        </div>
      </div>
    </Layout>
  );
}
